import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const PALETTE = [
  { border:'#3b82f6', bg:'#eff6ff', text:'#1e40af' },
  { border:'#22c55e', bg:'#f0fdf4', text:'#15803d' },
  { border:'#f59e0b', bg:'#fffbeb', text:'#92400e' },
  { border:'#a78bfa', bg:'#f5f3ff', text:'#5b21b6' },
  { border:'#ec4899', bg:'#fdf2f8', text:'#9d174d' },
  { border:'#14b8a6', bg:'#f0fdfa', text:'#0f766e' },
]

const DAY_NAMES = ['Lunes','Martes','Miércoles','Jueves','Viernes','Sábado','Domingo']
const DAY_SHORT = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom']

function dateKey(d) {
  return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0')
}

export default function WeekPrintReport({ dates, jobs, clients, staff, routeStaffMap, weekSummaryData, timesheetRows, onClose }) {
  const scheduledJobs = (jobs||[]).filter(j => j.status === 'scheduled')
  const dateKeys = dates.map(dateKey)

  // Distinct routes/teams this week, in a stable order, each given a color
  const routes = Array.from(new Set(scheduledJobs.filter(j=>j.route).map(j=>j.route))).sort()
  const routeColor = {}
  routes.forEach((r,i) => { routeColor[r] = PALETTE[i % PALETTE.length] })
  const noRouteJobs = scheduledJobs.filter(j=>!j.route)

  function clientName(id) {
    const c = clients.find(cl => cl.id === id)
    return c ? (c.nickname || c.name || '?') : '?'
  }

  function staffNamesFor(routeVal, dk) {
    const ids = (routeStaffMap && routeStaffMap[dk + '|' + routeVal]) || []
    return ids.map(id => (staff||[]).find(s => s.id === id)?.name?.split(' ')[0]).filter(Boolean)
  }

  function jobsFor(routeVal, dk) {
    return scheduledJobs
      .filter(j => j.scheduled_date === dk && j.route === routeVal)
      .sort((a,b) => (a.start_time||'').localeCompare(b.start_time||'') || 0)
  }

  const weekLabel = `${dates[0].toLocaleDateString('es-MX',{month:'short',day:'numeric'})} – ${dates[6].toLocaleDateString('es-MX',{month:'short',day:'numeric',year:'numeric'})}`

  // Group timesheet rows: route -> staffName -> rows (sorted by date)
  const rows = timesheetRows || []

  // ── Manual hours override (Horas pagadas) ──
  // Saved separately in timesheet_overrides. Clock In/Out data is never touched.
  const [overrides, setOverrides] = useState({}) // rowKey -> {hours, reason}
  const [drafts, setDrafts] = useState({})       // rowKey -> {hours:'text', reason:'text'} while typing
  const [savingKey, setSavingKey] = useState(null)
  function rowKey(row) { return `${row.date}|${row.staffName}|${row.clientName}` }
  useEffect(() => {
    if (!dateKeys.length) return
    supabase.from('timesheet_overrides').select('*')
      .gte('date', dateKeys[0]).lte('date', dateKeys[6])
      .then(({ data }) => {
        const m = {}
        ;(data||[]).forEach(o => { m[o.row_key] = { hours: o.hours, reason: o.reason || '' } })
        setOverrides(m)
      })
  }, [dateKeys[0]])
  // Accepts "3.5", "3h 30min", "3h", "30min", "3:30"
  function parseHours(txt) {
    const t = String(txt||'').trim().toLowerCase()
    if (!t) return null
    if (/^\d+:\d{1,2}$/.test(t)) { const [h,m] = t.split(':').map(Number); return h + m/60 }
    const hm = t.match(/(\d+(?:\.\d+)?)\s*h/), mm = t.match(/(\d+)\s*m/)
    if (hm || mm) return (hm ? parseFloat(hm[1]) : 0) + (mm ? parseInt(mm[1])/60 : 0)
    const n = parseFloat(t)
    return isNaN(n) ? null : n
  }
  function draftVal(key, field) {
    if (drafts[key]?.[field] !== undefined) return drafts[key][field]
    const o = overrides[key]
    if (!o) return ''
    return field === 'hours' ? fmtHM(o.hours) : (o.reason || '')
  }
  function setDraft(key, field, val) {
    setDrafts(prev => ({ ...prev, [key]: { ...prev[key], [field]: val } }))
  }
  async function saveOverride(row) {
    const key = rowKey(row)
    const d = drafts[key]
    if (!d) return
    const hoursTxt = d.hours !== undefined ? d.hours : (overrides[key] ? String(overrides[key].hours) : '')
    const reason = d.reason !== undefined ? d.reason : (overrides[key]?.reason || '')
    const hours = parseHours(hoursTxt)
    setSavingKey(key)
    if (hours == null && !reason.trim()) {
      // Both empty = remove the override (only the override row, never clock data)
      await supabase.from('timesheet_overrides').delete().eq('row_key', key)
      setOverrides(prev => { const n = { ...prev }; delete n[key]; return n })
    } else {
      await supabase.from('timesheet_overrides').upsert({
        row_key: key, date: row.date, staff_name: row.staffName, client_name: row.clientName,
        route: row.route != null ? String(row.route) : null,
        hours, reason: reason.trim() || null, updated_at: new Date().toISOString(),
      }, { onConflict: 'row_key' })
      setOverrides(prev => ({ ...prev, [key]: { hours, reason: reason.trim() } }))
    }
    setDrafts(prev => { const n = { ...prev }; delete n[key]; return n })
    setSavingKey(null)
  }
  function paidHours(row) {
    const o = overrides[rowKey(row)]
    return (o && o.hours != null) ? o.hours : (row.noLog ? 0 : (row.hours || 0))
  }
  const tsRoutes = Array.from(new Set(rows.map(r=>r.route))).sort()
  function rowsFor(routeVal, staffName) {
    return rows.filter(r=>r.route===routeVal && r.staffName===staffName).sort((a,b)=>a.date.localeCompare(b.date))
  }
  function staffNamesInRoute(routeVal) {
    return Array.from(new Set(rows.filter(r=>r.route===routeVal).map(r=>r.staffName))).sort()
  }
  function fmtTime(iso) {
    return new Date(iso).toLocaleTimeString('es-MX',{hour:'numeric',minute:'2-digit'})
  }
  function fmtHM(hrs) {
    if (hrs == null) return '—'
    const totalMin = Math.round(hrs * 60)
    const h = Math.floor(totalMin/60), m = totalMin % 60
    if (h === 0) return `${m}min`
    if (m === 0) return `${h}h`
    return `${h}h ${m}min`
  }

  return (
    <div style={ov.overlay} className="wpr-overlay">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .wpr-printarea, .wpr-printarea * { visibility: visible; }
          .wpr-printarea { position: absolute; left: 0; top: 0; width: 100%; }
          .wpr-noprint { display: none !important; }
          .wpr-section { page-break-inside: avoid; }
          .wpr-newpage { page-break-before: always; }
          .wpr-ovr { border: none !important; background: transparent !important; padding: 0 !important; }
          .wpr-ovr::placeholder { color: transparent; }
        }
      `}</style>
      <div style={ov.modal}>
        <div style={ov.header} className="wpr-noprint">
          <div style={{fontSize:16,fontWeight:700}}>🖨️ Reporte de la semana</div>
          <div style={{display:'flex',gap:8}}>
            <button style={ov.printBtn} onClick={()=>window.print()}>🖨️ Imprimir / Guardar PDF</button>
            <button style={ov.closeBtn} onClick={onClose}>×</button>
          </div>
        </div>

        <div style={ov.body} className="wpr-printarea">
          <div style={{textAlign:'center',marginBottom:20}}>
            <div style={{fontSize:20,fontWeight:700,color:'#202124'}}>Kitty's Cleaning Services</div>
            <div style={{fontSize:13,color:'#80868b',marginTop:2}}>Semana del {weekLabel}</div>
          </div>

          {/* ── Team schedule, day by day ── */}
          <div className="wpr-section" style={{marginBottom:28}}>
            <div style={ov.sectionTitle}>Horario por equipo</div>
            <table style={ov.table}>
              <thead>
                <tr>
                  <th style={ov.th}>Equipo</th>
                  {dates.map((d,i)=>(
                    <th key={i} style={ov.th}>{DAY_SHORT[i]} {d.getDate()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {routes.map(r => {
                  const col = routeColor[r]
                  return (
                    <tr key={r}>
                      <td style={{...ov.tdLabel, borderLeft:`4px solid ${col.border}`, background:col.bg, color:col.text}}>
                        Ruta {r}
                      </td>
                      {dateKeys.map(dk => {
                        const dj = jobsFor(r, dk)
                        const names = staffNamesFor(r, dk)
                        return (
                          <td key={dk} style={ov.td}>
                            {names.length>0 && (
                              <div style={{fontSize:9,fontWeight:700,color:col.text,background:col.bg,display:'inline-block',padding:'1px 6px',borderRadius:99,marginBottom:4}}>
                                {names.join(', ')}
                              </div>
                            )}
                            {dj.length===0 ? <span style={{color:'#ddd'}}>—</span> : dj.map(j=>(
                              <div key={j.id} style={{marginBottom:3,fontSize:11}}>
                                <div style={{fontWeight:600}}>{clientName(j.client_id)}</div>
                                <div style={{color:'#80868b'}}>{j.start_time||'Todo el día'} · {j.hours||'?'}h</div>
                              </div>
                            ))}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
                {noRouteJobs.length>0 && (
                  <tr>
                    <td style={{...ov.tdLabel, borderLeft:'4px solid #bdc1c6', background:'#fafafa', color:'#80868b'}}>Sin equipo</td>
                    {dateKeys.map(dk => {
                      const dj = noRouteJobs.filter(j=>j.scheduled_date===dk)
                      return (
                        <td key={dk} style={ov.td}>
                          {dj.length===0 ? <span style={{color:'#ddd'}}>—</span> : dj.map(j=>(
                            <div key={j.id} style={{marginBottom:3,fontSize:11}}>
                              <div style={{fontWeight:600}}>{clientName(j.client_id)}</div>
                              <div style={{color:'#80868b'}}>{j.start_time||'Todo el día'} · {j.hours||'?'}h</div>
                            </div>
                          ))}
                        </td>
                      )
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Individual hours, day by day ── */}
          <div className="wpr-section">
            <div style={ov.sectionTitle}>Horas por empleada</div>
            <div style={{fontSize:10,color:'#aaa',marginBottom:8}}>Programado · <span style={{color:'#1d9e75',fontWeight:700}}>real (Clock In/Out)</span></div>
            {!weekSummaryData ? (
              <div style={{color:'#9aa0a6',padding:16}}>Cargando...</div>
            ) : weekSummaryData.length===0 ? (
              <div style={{color:'#9aa0a6',padding:16}}>Sin rutas publicadas esta semana</div>
            ) : (
              <table style={ov.table}>
                <thead>
                  <tr>
                    <th style={ov.th}>Empleada</th>
                    {dates.map((d,i)=>(<th key={i} style={ov.th}>{DAY_SHORT[i]} {d.getDate()}</th>))}
                    <th style={ov.th}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {weekSummaryData.map(s => (
                    <tr key={s.name}>
                      <td style={ov.tdLabel}>{s.name}</td>
                      {dateKeys.map(dk => {
                        const sched = s.days[dk] || 0
                        const actual = s.actualDays?.[dk]
                        const offBy = actual!=null ? Math.abs(actual - sched) : 0
                        return (
                          <td key={dk} style={{...ov.td, textAlign:'center'}}>
                            {sched ? sched.toFixed(1) : <span style={{color:'#ddd'}}>—</span>}
                            {actual!=null && (
                              <div style={{fontSize:10,fontWeight:700,color:offBy>=0.5?'#e8547a':'#1d9e75',marginTop:1}}>
                                {actual.toFixed(1)}
                              </div>
                            )}
                          </td>
                        )
                      })}
                      <td style={{...ov.td, textAlign:'center'}}>
                        <div style={{fontWeight:700,color:'#e8547a'}}>{s.totalHours.toFixed(1)}</div>
                        {s.actualTotal!=null && (
                          <div style={{fontSize:10,fontWeight:700,color:'#1d9e75',marginTop:1}}>{s.actualTotal.toFixed(1)}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Hoja de tiempos: Clock In/Out por Ruta y Persona ── */}
          <div className="wpr-section wpr-newpage" style={{marginTop:28}}>
            <div style={ov.sectionTitle}>🕐 Hoja de tiempos — Clock In/Out por Ruta y Persona</div>
            <div style={{fontSize:11,color:'#aaa',marginBottom:14}}>
              Para uso de nómina: compare las horas trabajadas (Clock In/Out) contra lo pagado.
            </div>
            {tsRoutes.length===0 ? (
              <div style={{color:'#9aa0a6',padding:16}}>Sin datos de Clock In/Out esta semana todavía.</div>
            ) : tsRoutes.map(r => {
              const col = routeColor[r] || PALETTE[0]
              const names = staffNamesInRoute(r)
              return (
                <div key={r} style={{marginBottom:20}}>
                  <div style={{fontSize:13,fontWeight:800,color:col.text,background:col.bg,display:'inline-block',padding:'3px 10px',borderRadius:6,marginBottom:8,borderLeft:`4px solid ${col.border}`}}>
                    Ruta {r}
                  </div>
                  {names.map(name => {
                    const personRows = rowsFor(r, name)
                    const weekTotal = personRows.reduce((sum,row)=>sum+(row.hours||0),0)
                    const paidTotal = personRows.reduce((sum,row)=>sum+paidHours(row),0)
                    const missingCount = personRows.filter(row=>row.noLog && !overrides[rowKey(row)]).length
                    const adjustedCount = personRows.filter(row=>overrides[rowKey(row)]).length
                    return (
                      <div key={name} style={{marginBottom:14,marginLeft:6}}>
                        <div style={{fontSize:12,fontWeight:700,marginBottom:4}}>{name}</div>
                        <table style={ov.table}>
                          <thead>
                            <tr>
                              <th style={ov.th}>Día</th>
                              <th style={ov.th}>Casa</th>
                              <th style={ov.th}>Entrada</th>
                              <th style={ov.th}>Salida</th>
                              <th style={ov.th}>Horas</th>
                              <th style={{...ov.th,color:'#e8547a'}}>Horas pagadas</th>
                              <th style={ov.th}>Nota</th>
                            </tr>
                          </thead>
                          <tbody>
                            {personRows.map((row,i) => {
                              const key = rowKey(row)
                              const adj = overrides[key]
                              return (
                              <tr key={i} style={adj?{background:'#fdf0f3'}:row.noLog?{background:'#fff8e1'}:{}}>
                                <td style={ov.td}>{new Date(row.date+'T12:00:00').toLocaleDateString('es-MX',{weekday:'short',day:'numeric'})}</td>
                                <td style={ov.td}>{row.clientName}</td>
                                {row.noLog ? (
                                  <td colSpan={3} style={{...ov.td,color:adj?'#80868b':'#92700e',fontWeight:adj?400:700}}>{adj?'Sin registro (ajustado ✍️)':'⚠️ Sin registro'}</td>
                                ) : (
                                  <>
                                    <td style={ov.td}>{fmtTime(row.clockIn)}</td>
                                    <td style={ov.td}>{fmtTime(row.clockOut)}</td>
                                    <td style={{...ov.td,textAlign:'center'}}>{fmtHM(row.hours)}</td>
                                  </>
                                )}
                                <td style={{...ov.td,borderBottom:'1px solid #ddd'}}>
                                  <input className="wpr-ovr" value={draftVal(key,'hours')}
                                    placeholder={row.noLog ? 'hrs' : fmtHM(row.hours)}
                                    onChange={e=>setDraft(key,'hours',e.target.value)}
                                    onBlur={()=>saveOverride(row)}
                                    onKeyDown={e=>{ if(e.key==='Enter') e.target.blur() }}
                                    style={{...ov.ovrInput,width:70,fontWeight:adj?700:400,color:adj?'#e8547a':'#202124'}}/>
                                  {savingKey===key && <span style={{fontSize:9,color:'#9aa0a6',marginLeft:4}}>...</span>}
                                </td>
                                <td style={{...ov.td,borderBottom:'1px solid #ddd'}}>
                                  <input className="wpr-ovr" value={draftVal(key,'reason')}
                                    placeholder="razón (opcional)"
                                    onChange={e=>setDraft(key,'reason',e.target.value)}
                                    onBlur={()=>saveOverride(row)}
                                    onKeyDown={e=>{ if(e.key==='Enter') e.target.blur() }}
                                    style={{...ov.ovrInput,width:'100%',minWidth:120,fontSize:11,color:'#5f6368'}}/>
                                </td>
                              </tr>
                              )
                            })}
                            <tr>
                              <td colSpan={4} style={{...ov.td,textAlign:'right',fontWeight:700}}>
                                Total de la semana{missingCount>0?` (${missingCount} sin registro)`:''}{adjustedCount>0?` · ${adjustedCount} ajustado${adjustedCount>1?'s':''}`:''}
                              </td>
                              <td style={{...ov.td,textAlign:'center',fontWeight:700,color:'#1d9e75'}}>{fmtHM(weekTotal)}</td>
                              <td style={{...ov.td,fontWeight:800,color:'#e8547a',fontSize:13}}>{fmtHM(paidTotal)}</td>
                              <td style={ov.td}/>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

const ov = {
  overlay: {position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center'},
  modal: {background:'#fff',borderRadius:14,width:'94vw',maxWidth:1100,maxHeight:'92vh',display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'},
  header: {display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',borderBottom:'1px solid #e3e5e8',flexShrink:0},
  printBtn: {padding:'8px 16px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:600,fontSize:13,cursor:'pointer'},
  closeBtn: {fontSize:22,border:'none',background:'none',cursor:'pointer',color:'#9aa0a6'},
  body: {overflowY:'auto',padding:'24px 28px'},
  sectionTitle: {fontSize:14,fontWeight:700,color:'#202124',marginBottom:10,borderBottom:'2px solid #e3e5e8',paddingBottom:6},
  table: {width:'100%',borderCollapse:'collapse'},
  th: {textAlign:'left',fontSize:11,color:'#80868b',fontWeight:600,padding:'6px 8px',borderBottom:'2px solid #eee',whiteSpace:'nowrap'},
  td: {verticalAlign:'top',padding:'6px 8px',borderBottom:'1px solid #f2f2f2',fontSize:12},
  ovrInput: {padding:'2px 5px',border:'1px solid #bdc1c6',borderRadius:3,fontSize:12,background:'#fff',fontFamily:'inherit'},
  tdLabel: {verticalAlign:'top',padding:'6px 8px',borderBottom:'1px solid #f2f2f2',fontSize:12,fontWeight:600,whiteSpace:'nowrap'},
}
